import { ApolloLink, Observable, Operation, FetchResult } from '@apollo/client';
import { print } from 'graphql/language/printer';
import promiseClient, { PromiseClient } from 'hub-http/adapters/promiseClient';
import { getResponseHeaders } from 'hub-http/adapters/adapterUtils';
import { getGqlEarlyRequest } from './quickFetch';
import enviro from 'enviro';
import { Metrics } from './internal/Metrics';
import { stableStringify } from './internal/stableStringify';

interface UriFunction {
  (operation: Operation): string;
}

interface BatchedOperation {
  operation: Operation;
  observer: {
    next(value: any): void;
    error(errorValue: any): void;
    complete(): void;
  };
}

const earlyRequestLink = new ApolloLink((operation, forward) => {
  const earlyRequest = getGqlEarlyRequest(operation);
  if (earlyRequest) {
    operation.setContext({
      earlyRequest,
    });
  }

  return forward(operation);
});

const toRequestPayload = (
  operation: Operation,
  experimentalPersistedQueries: boolean
) => {
  const { operationName, variables, query } = operation;
  const executionInput = {
    operationName,
    variables,
  } as any;

  if (experimentalPersistedQueries && enviro.deployed()) {
    executionInput.id = (query as any).id;
  } else {
    executionInput.query = print(query);
  }
  return executionInput;
};

// TODO: add `x-hubspot-correlation-id` to the allowlist of headers in `Access-Control-Expose-Headers`
const getCorrelationId = (xhr: XMLHttpRequest | null): string | null => {
  if (!xhr) {
    return null;
  }

  // requesting a header which is not present (or isn't in the `Access-Control-Expose-Headers` allowlist)
  // via `xhr.getResponseHeader()` causes chrome to log an error: `Refused to get unsafe header`
  // Getting the correlation id is best-effort, so use this method to read headers silently.
  const headers = getResponseHeaders(xhr);
  const serverTiming = headers['server-timing'];

  // server-timing: hcid;desc=54545bee-6899-4c90-80b0-812e513f83ed
  const match = (serverTiming || '').match(/^hcid;desc=(.*)$/);
  return match ? match[1] : null;
};

const createHubHttpLinkForClientHelper = (
  client: PromiseClient,
  uri: string | UriFunction,
  experimentalPersistedQueries: boolean
) => {
  const httpLink = new ApolloLink((operation) => {
    const { hubHttpOptions, earlyRequest } = operation.getContext() as {
      earlyRequest?: Promise<FetchResult> | null;
      hubHttpOptions: Record<string, any>;
    };

    const chosenURI = typeof uri === 'function' ? uri(operation) : uri;

    return new Observable((observer) => {
      let currentXhr: XMLHttpRequest | null = null;

      const makeRequest = () =>
        client.post<FetchResult>(chosenURI, {
          ...hubHttpOptions,
          withXhr(xhr: XMLHttpRequest) {
            currentXhr = xhr;
          },
          data: toRequestPayload(operation, experimentalPersistedQueries),
        });

      const request = earlyRequest
        ? earlyRequest.catch(makeRequest)
        : Metrics.timer('query-duration').time(makeRequest);

      const metricDimensions = { earlyRequest: String(!!earlyRequest) };

      request
        .then((data) => {
          if (data.errors && data.errors.length > 0) {
            Metrics.counter(
              'queries-with-errors',
              metricDimensions
            ).increment();
          } else {
            Metrics.counter('successful-queries', metricDimensions).increment();
          }

          const correlationId = getCorrelationId(currentXhr);
          if (correlationId) {
            operation.setContext({
              correlationId,
            });
          }

          observer.next(data);
          observer.complete();
        })
        .catch((err) => {
          if (err.errorCode === 'ABORT') {
            return;
          }

          const correlationId = getCorrelationId(currentXhr);
          if (correlationId) {
            operation.setContext({
              correlationId,
            });
          }

          Metrics.counter('failed-queries', metricDimensions).increment();

          observer.error(err);
        });

      return () => {
        if (currentXhr && currentXhr.readyState !== 4) {
          currentXhr.abort();
        }
      };
    });
  });

  return httpLink;
};

export interface HubHttpLinkOptions {
  uri?: string | UriFunction;
  experimentalPersistedQueries?: boolean;
}

export const createHubHttpLinkForClient =
  (client: PromiseClient) =>
  ({
    uri = '',
    experimentalPersistedQueries = false,
  }: HubHttpLinkOptions = {}) => {
    const httpLink = createHubHttpLinkForClientHelper(
      client,
      uri,
      experimentalPersistedQueries
    );

    return ApolloLink.from([earlyRequestLink, httpLink]);
  };

export interface BatchedHubHttpLinkOptions {
  uri?: string;
  experimentalPersistedQueries?: boolean;
  batchInterval?: number;
  batchMax?: number;
}

interface BatchedOperationsWithOptions {
  options: Record<string, any>;
  requests: BatchedOperation[];
}

/**
 * We allow consumers to pass hub-http options as a context value to any
 * query or mutation. Handling this for a single operation is roughly trivial
 * (see {@link createHubHttpLinkForClientHelper}), but for a batched operation
 * this has some complexity because multiple items in the batch may have
 * different options set. Merging all of those options is undesired, as it
 * means a header set by a query in a library could override headers set by an
 * app, for example, dependning on how the merge code is written.
 *
 * Instead, we iterate through a batch, partitioning the operations by options.
 * In local testing, the performance impact of this iteration, even on large
 * batches, was not observable.
 *
 * In theory, this should have no impact on request performance as the requests
 * are multiplexed (we don't want for a request to complete before dispatching
 * the next). Event loop conflicts could arise, but in local testing that was
 * not the case.
 */
function partitionOperationsByHeaders(
  queue: BatchedOperation[]
): BatchedOperationsWithOptions[] {
  const operationsByStringifiedOptions = new Map<
    string,
    BatchedOperationsWithOptions
  >();
  for (const queuedReq of queue) {
    const { hubHttpOptions } = queuedReq.operation.getContext();
    const stringOptions = stableStringify(hubHttpOptions);
    if (operationsByStringifiedOptions.has(stringOptions)) {
      operationsByStringifiedOptions
        .get(stringOptions)!
        .requests.push(queuedReq);
    } else {
      operationsByStringifiedOptions.set(stringOptions, {
        options: hubHttpOptions,
        requests: [queuedReq],
      });
    }
  }
  return Array.from(operationsByStringifiedOptions.values());
}

export const createBatchedHubHttpLinkForClient =
  (client: PromiseClient) =>
  ({
    uri = '',
    batchInterval = 10,
    batchMax = 10,
    experimentalPersistedQueries = false,
  }: BatchedHubHttpLinkOptions = {}) => {
    let queue: BatchedOperation[] = [];
    let dispatchTimer: number;

    function dispatchQueue() {
      const batch = queue;
      queue = [];

      const partitionedBatches = partitionOperationsByHeaders(batch);

      partitionedBatches.forEach(({ options, requests }) => {
        let currentXhr: XMLHttpRequest | null = null;
        client
          .post<FetchResult[]>(uri, {
            ...options,
            data: requests.map(({ operation }) =>
              toRequestPayload(operation, experimentalPersistedQueries)
            ),
            withXhr(xhr: XMLHttpRequest) {
              currentXhr = xhr;
            },
          })
          .then((data) => {
            const correlationId = getCorrelationId(currentXhr);

            requests.forEach(({ observer, operation }, i) => {
              if (correlationId) {
                operation.setContext({
                  correlationId,
                });
              }

              observer.next(data[i]);
              observer.complete();
            });
          })
          .catch((err) => {
            const correlationId = getCorrelationId(currentXhr);

            requests.forEach(({ observer, operation }) => {
              if (correlationId) {
                operation.setContext({
                  correlationId,
                });
              }

              observer.error(err);
            });
          });
      });
    }

    const batchedHttpLink = new ApolloLink((operation) => {
      return new Observable((observer) => {
        queue.push({
          operation,
          observer,
        });

        if (queue.length === batchMax) {
          clearTimeout(dispatchTimer);
          dispatchQueue();
        } else if (queue.length === 1) {
          dispatchTimer = setTimeout(() => {
            dispatchQueue();
          }, batchInterval);
        }
        // notice there is no cleanup function so requests won't be aborted when query is unwatched.
      });
    });

    const httpLink = createHubHttpLinkForClientHelper(
      client,
      uri,
      experimentalPersistedQueries
    );

    return ApolloLink.from([
      earlyRequestLink,
      ApolloLink.split(
        (operation) => {
          const context = operation.getContext();
          // There are some scenarios where we want to fallback to not batching. We could
          // maintain separate queues like the official Apollo batching link but seems overkill.
          return (
            typeof uri === 'string' && !context.noBatch && !context.earlyRequest
          );
        },
        batchedHttpLink,
        httpLink
      ),
    ]);
  };

export const createHubHttpLink = (clientStack: any) =>
  createHubHttpLinkForClient(promiseClient(clientStack));

export const createBatchedHubHttpLink = (clientStack: any) =>
  createBatchedHubHttpLinkForClient(promiseClient(clientStack));
